import { Link } from "react-router-dom";
import React, { ReactNode } from 'react';
import { Column, ColumnType } from "../../../types/table";

interface CellContainerProps {
  row: { id: string; [key: string]: any };
  column: Column;
  className?: string;
}

const CellContainer: React.FC<React.PropsWithChildren<CellContainerProps>> = ({
  column,
  row,
  className,
  children,
}) => {
  return (
    <div
      slot={`row-${row.id}-column-${column.id}`}
      className={`${className ? className : ''} min-h-8 flex items-center`}
    >
      {children}
    </div>
  );
};

/**
 * Safe parsing for the common backend shapes we expect.
 * Avoids relying on `new Date(string)` for non-ISO variants.
 */
const parseDateValue = (value: unknown): Date | null => {
  if (value == null) return null;
  if (typeof value !== 'string') {
    // If something already formatted to a React element/string by column.format,
    // we should not attempt to parse it as a date here.
    return null;
  }

  const v = value.trim();
  if (!v) return null;

  // YYYY-MM-DD
  const mDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (mDate) {
    const [, y, mo, d] = mDate;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // YYYY-MM-DDTHH:mm  (datetime-local)
  const mDT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v);
  if (mDT) {
    const [, y, mo, d, h, mi] = mDT;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Full ISO fallback (e.g. ...Z or ...+00:00)
  const dt = new Date(v);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const cellTemplate = (columnConfig: Column, row: any, data?: any) => {
  // Use formatter if provided; it may return string OR JSX.Element.
  const raw = row?.[columnConfig.id];
  const value = columnConfig.format ? columnConfig.format(raw) : raw;

  if (columnConfig.renderCell) {
    return (
      <CellContainer
        row={row}
        column={columnConfig} 
        key={`row-${row.id}-column-${columnConfig.id}`}
      >
        {columnConfig.renderCell({
          data: row,
          column: columnConfig,
        }) as ReactNode}
      </CellContainer>
    );
  }

  switch (columnConfig.columnType) {
    case ColumnType.TitleLink:
      return (
        <CellContainer
          row={row}
          column={columnConfig}
          key={`row-${row.id}-column-${columnConfig.id}`}
          className="box-content font-semibold hover:text-link hover:underline cursor-pointer"
        >
          <Link to={data?.titleLink}>{value}</Link>
        </CellContainer>
      );

    case ColumnType.Date:
    case ColumnType.DateTime: {
      // IMPORTANT:
      // - If `columnConfig.format` exists, it should have already produced display output.
      // - Do NOT re-parse it with `new Date(...)` (that causes Invalid Date).
      if (columnConfig.format) {
        return (
          <CellContainer
            row={row}
            column={columnConfig}
            key={`row-${row.id}-column-${columnConfig.id}`}
          >
            {value as any}
          </CellContainer>
        );
      }

      // Fallback only if no formatter provided
      const d = parseDateValue(raw);
      const displayString =
        d
          ? (columnConfig.columnType === ColumnType.DateTime
            ? d.toLocaleString('en-US')
            : d.toLocaleDateString('en-US'))
          : '';

      return (
        <CellContainer
          row={row}
          column={columnConfig}
          key={`row-${row.id}-column-${columnConfig.id}`}
        >
          {displayString}
        </CellContainer>
      );
    }

    case ColumnType.Number:
    case ColumnType.Currency:
      return (
        <CellContainer
          row={row}
          column={columnConfig}
          key={`row-${row.id}-column-${columnConfig.id}`}
        >
          <div className="text-right">{value}</div>
        </CellContainer>
      );

    default:
      return (
        <CellContainer
          row={row}
          column={columnConfig}
          key={`row-${row.id}-column-${columnConfig.id}`}
        >
          {value}
        </CellContainer>
      );
  }
};

export default cellTemplate;
export { CellContainer };